import React from 'react';
import { Box, Typography, Accordion, AccordionSummary, AccordionDetails, Link } from '@mui/material';
import { useTranslation } from 'react-i18next';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MobileScrollableContent from './shared/MobileScrollableContent';
import { MOBILE_SETTINGS_STYLES } from './shared/MobileSettingsStyles';
import { useMobileSettingsNavigation } from './shared/hooks/useMobileSettingsNavigation';
import MobilePageContent from 'app/pages/mobile/mobile-layout/MobilePageContent';
import { BACKGROUND_SIZES_v2 } from 'app/pages/mobile/utils/constants';

// Inline link parser for markdown-style links [text](url) with URL allowlist
const isSafeUrl = (url: string) => /^(https?:\/\/|mailto:)/i.test(url);

const parseInlineLinks = (content: string) => {
  // Parse markdown-style links [text](url) and render them inline
  const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
  const parts = content.split(linkRegex);

  return parts.map((part, index) => {
    if (index % 3 === 0) {
      // Regular text
      return part;
    } else if (index % 3 === 1) {
      // Link text
      const url = parts[index + 1];

      // Security check - only allow safe URLs
      if (!isSafeUrl(url)) {
        return (
          <span
            key={index}
            style={{
              color: 'inherit',
              textDecoration: 'none',
            }}
          >
            {part}
          </span>
        );
      }

      return (
        <Link
          key={index}
          href={url}
          target={url.startsWith('mailto:') ? '_self' : '_blank'}
          rel={url.startsWith('mailto:') ? undefined : 'noopener noreferrer'}
          onClick={
            url.startsWith('mailto:')
              ? (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  // Open email client in same window
                  window.location.href = url;
                }
              : undefined
          }
          sx={MOBILE_SETTINGS_STYLES.linkText}
        >
          {part}
        </Link>
      );
    }
    return null;
  });
};

// Section keys for Privacy Policy
const privacyPolicySectionKeys = [
  'introduction',
  'whoWeAre',
  'scope',
  'informationCollect',
  'howWeUse',
  'legalBases',
  'sharingData',
  'cookiesTracking',
  'dataSecurity',
  'internationalTransfers',
  'yourRights',
  'updates',
  'contact',
  'additional',
];

/**
 * MobilePrivacyPolicyPage - Privacy Policy page for mobile app
 * Has header with back button but no footer navigation
 */
function MobilePrivacyPolicyPage(): JSX.Element {
  const { t } = useTranslation();
  const { handleBackSettings } = useMobileSettingsNavigation();
  return (
    <MobilePageContent
      transparentHeight={BACKGROUND_SIZES_v2.SHOW_HEADER}
      title={t('mobilePrivacyPolicyTitle')}
      onBack={handleBackSettings}
      sx={{ p: 0 }}
    >
      {/* Content Section */}
      <Box
        sx={{
          flex: 1, // Take up all available space
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden', // Prevent content from overlapping
        }}
      >
        {/* Scrollable Content */}
        <MobileScrollableContent paddingBottom="210px">
          {/* Privacy Policy Sections as Accordions */}
          {privacyPolicySectionKeys.map((sectionKey, index) => (
            <Accordion
              key={sectionKey}
              sx={{
                ...MOBILE_SETTINGS_STYLES.accordionContainer,
                borderBottom:
                  index === privacyPolicySectionKeys.length - 1 ? 'none' : '1px solid rgba(0, 0, 0, 0.12)',
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  minHeight: '64px',
                  px: 3, // Add horizontal padding for content
                  '&.Mui-expanded': {
                    minHeight: '64px',
                  },
                  '& .MuiAccordionSummary-content': {
                    margin: '16px 0',
                  },
                }}
              >
                <Typography sx={MOBILE_SETTINGS_STYLES.accordionTitle}>
                  {t(`privacyPolicyContent.${sectionKey}.title`)}
                </Typography>
              </AccordionSummary>
              <AccordionDetails
                sx={{
                  pt: 0,
                  pb: 2,
                  px: 3, // Add horizontal padding for content
                }}
              >
                <Typography sx={MOBILE_SETTINGS_STYLES.accordionContent}>
                  {parseInlineLinks(t(`privacyPolicyContent.${sectionKey}.content`))}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </MobileScrollableContent>
      </Box>
    </MobilePageContent>
  );
}

export default MobilePrivacyPolicyPage;
